<template>
	<!-- 
		UI Edit automodal

		## Version 1.10 (IMPORTANTE!)
		Corrige la desactualización automática desde formArchetype. Requiere un ID MD5 especial en el 
		formato de formKey.

		## Version 1.9
		Emite el evento @hidden.

		## Version 1.8
		Agergado el parémtro formId, que mejora la compatibilidad del 
		borrado general de elementos cuando éstos pueden incluir el id en otro formato.

		## Version 1.7
		Corrige un bug que impedía la preDefinición del objeto FORM.

		## Version 1.6
		Agregado el slot bottom

		## Version 1.5
		Con la función set(Form), se puede actualizar el contenido de form desde afuera.

		## Version 1.4
		Ahora emite en timpo real los datos en form como input update (para un v-model:binding correcto)

		## Version 1.3
		Agrega la propiedad no-enforce-focus al constructor de b-modal para mejorar la compatibilidad con 
			mobiscroll y ahora dispara el evento
			@defined, cuando ha terminado de cargar la información del modal/asides. 
		Remoción de autocierre del modal en backdrop click.
		Agregados los eventos:
			@display 	(Mostrado cuando se ha desplegado el modal)
			@closed		(Mostrado cuando se ha cerrado el modal)

		## Version 1.2
		Almacena todo el contenido de asides localmente y lo vuelve accesibe desde la funcion getAsides()

		## Version 1.11
		Ahora se puede etiquetar diferentes botones con las propiedades:
			cancel-label
			processing-label
			close-label
			delete-label

		## Version 1.10
		Ahora se puede establercer el estatus de isProcessing interno del módal víasetProcesing()

		## Version 1.9
		Fix que permite la actualización del valor local desde afuera

		## Version 1.8
		Ajustes para formato selectmultiple

		## Version 1.7
		Envía con el request creación/edición una copia de los parametros en $route.

		## Version 1.6
		:allowAside Permite la visualización de un slot aside! Corrección de error de slot.

		## Version 1.5
		ViewOnly y AllowDelete ahora son propiedades independientes. (AllowDelete requiere formId definido)

		## Version 1.4
		El botón de borrado no aparece si no hay un ID definido previo, además permite el parámetro viewOnly

		## Version 1.3
		Permite que se le pase un parámetro value (v-model) para que el contenedor sepa 
		los datos que el componente está trabajando/editando.

		## Version 1.2
		Esta versión permite ocultar el botón de borrado.

		## Version 1.1
		Esta nueva versión permite la sobreescritura vía btn class del botón principal, 
		además permite la confirmación de borrado con un disclaimer natural.

		## Sample:
		<ui-edit-auto-modal :refresh="refresh" :formArchetype="formArchetype" label="Editar" 
			:handler="'define/source/configuraciones/' + data.item.id" apply-button="Actualizar" size="md">
			<i class="fa fa-pencil"></i>
		</ui-edit-auto-modal>

		## Props:

		hanlder: (Requerido, String, URL)
			URL de control de datos, por ejemplo: 
				+ define/source/configuraciones (para crear una nueva entrada en la tabla configuraciones)
				+ define/source/configuraciones/:id (para realizar la prelectura de tabla y acutalizar el registro)

		form-archetype: (Requerido, Array)
			Array con cada uno de los elementos a editar, compatible con ui-input-handler, p: e:
			[
				{ key: "id", type: "hidden" },
				{ key: "promocion_nombre", label: 'Nombre de la promoción' },
				{ key: "imagenes_id", type: "image", label: 'Imágen del producto' },
			]

		formOverride: (Opcional, Objeto)
			Permite la reescritura de algun valor desde la invocación del compomente.  P. ej:
			{
				algunaClave; 'Siempre enviará este valor'
			}

		refresh: (Opcional)
			Callback a llamar al finalizar un modal

		allowDelete: (Opcional, Bolean) [Default true]
			Permite deshabilitar la capacidad de borrado del registro en BDD. 

		deleteDisclaimer: (Opcional, String) [Default "Ésta acción no se puede deshacer"]
			Mensaje textual que aparecerá en la confirmación de borrado.
		
		btn-class: (Opcional)
			Clase para sobreescribr en el boton del lanzandor

		apply-button: (Opcional)
			Etiqueta en el botón de submit

		@Author: Daniel Lepe
		@Date: 01/03/2022
	-->
	<div class="ui-edit-auto-modal" :class="btnClass" @click.stop.prevent="openModal" no-enforce-focus>

		<b v-if="isLoading" key="loading">
			<i class="fa fa-spin fa-cog"></i>
		</b>

		<template v-else>
			<slot name="default">
				<b key="normal">{{ label }}</b>
			</slot>
		</template>

		<b-modal v-if="isLoaded" :id="currentId" :size="size" :title="label" v-model="isModalOn" scrollable @hidden="cleanModal" no-enforce-focus no-close-on-backdrop no-close-on-esc>
			<b-row>
				<b-col>
					<b-form @submit="onSubmit($event)" :action="handler" :key="formKey">
						<template v-for="(fArch, $index) in formArchetype">
							<div class="separator" :key="$index" v-if="fArch == null" >
								<hr />
							</div>
							<template v-else-if=" lodash.isArray(fArch) || fArch.type != 'hidden' " >
								<b-row :key="$index" v-if="lodash.isArray(fArch)" >
									<b-col v-for="fArchInRow in fArch" :key="fArchInRow.key" >
										<ui-input-handler @blur="$emit('blur', $event)" @focus="$emit('focus')" :ref="fArchInRow.key" :f-arch="fArchInRow" v-model="form[fArchInRow.key]" :source="source" />
									</b-col>
								</b-row>
								<b-row v-else-if="fArch.type != 'hidden'" :key="fArch.key" >
									<b-col>
										<ui-input-handler @blur="$emit('blur', $event)" @focus="$emit('focus')" :ref="fArch.key" :f-arch="fArch" v-model="form[fArch.key]" :source="source" />
									</b-col>
								</b-row>
							</template>
						</template>
					</b-form>
				</b-col>
				<slot name="aside" v-if="allowAside">
					<b-col md="auto">
						slotAside! <br />
						b-col recommended.
					</b-col>
				</slot>
			</b-row>

			<slot name="bottom"></slot>

			<template v-slot:modal-footer="{ cancel }">
				<template v-if="!isProcessing">
					<b-button-group size="sm" v-if="!!formId && allowDelete && allowDelete != 'false'">
						<b-button @click="delItem($event)" variant="danger">
							{{ deleteLabel }}
						</b-button>
					</b-button-group>

					<template v-if="viewOnly">
						<b-button-group size="sm">
							<b-button @click="cancel()">
								{{ closeLabel }}
							</b-button>
						</b-button-group>
					</template>

					
					<b-button-group v-else size="sm">
						<b-button @click="cancel()">{{cancelLabel}}</b-button>
						<slot name="extra-buttons"></slot>
						<b-button @click="onSubmit($event)" variant="primary">
							{{ applyButton }}
						</b-button>
					</b-button-group>
				</template>

				<b-button-group size="sm" v-else>
					<b-button variant="primary" disabled> {{processingLabel}}... </b-button>
				</b-button-group>
			</template>

		</b-modal>

	</div>
</template>

<script>
import _ from "lodash";
import axios from "axios";
import mobiscroll from "@mobiscroll/javascript";
import uiInputHandler from "./ui-input-handler.vue";
import swal2 from "sweetalert2";
import swal from "sweetalert";

const Toast = swal2.mixin({
	toast: true,
	position: "top-end",
	showConfirmButton: false,
	grow: "column",
	timer: 3000,
});

const extractForm = (formArchetype) => {
	let ret = {};
	_.forEach(formArchetype, (field) => {
		if (field != null && _.isArray(field)) {
			_.forEach(field, (subField) => {
				ret[subField.key] = !!subField.default
					? subField.default
					: null;
			});
		} else if (field != null && !!field.key) {
			ret[field.key] = !!field.default ? field.default : null;
		}
	});
	return ret;
};

export default {
	components: { uiInputHandler },
	name: "UIEditAutoModal",
	props: {
		formId: {
			type: [Number, String],
		},
		cancelLabel: {
			type: String,
			default: 'Cancelar'
		},
		processingLabel: {
			type: String,
			default: 'Processing'
		},
		closeLabel: {
			type: String,
			default: 'Close'
		},
		deleteLabel: {
			type: String,
			default: 'Borrar'
		},
		applyButton: {
			type: [String, Boolean, Number],
			default: "Guardar",
		},
		label: {
			type: [String, Boolean, Number],
			default: "Editar",
		},
		deleteDisclaimer: {
			type: String,
			default: "This action can not be undone",
		},
		btnClass: {
			type: String,
			default: "btn btn-primary",
		},
		allowAside: {
			type: Boolean,
			default: false,
		},
		viewOnly: {
			type: Boolean,
			default: false,
		},
		value: {
			type: [Array, Object],
		},
		allowDelete: {
			type: [String, Number, Boolean],
			default: true,
		},

		refresh: {
			type: [Function],
		},
		buttonSize: {
			type: [String],
			default: () => "sm",
		},
		handler: {
			type: [String],
			required: true,
		},
		size: {
			type: [String],
			default: "md",
		},
		formKey: {
			type: String,
			default: 'formKey'
		},
		formArchetype: {
			type: [Array],
			default: () => {
				return [
					{
						key: "id",
						value: null,
						default: null,
						label: "",
						readonly: false,
						type: null, // [hidden|number|price|phone|textarea|positivo|password|email]
					},
				];
			},
		},
		formOverride: {
			type: [Object],
			default: () => {
				return {};
			},
		},
	},
	computed: {},
	methods: {
		set(value){
			this.form = _.clone(value)
			this.$emit('input', this.form)
		},
		getAsides(){
			return this.asides;
		}, 
		triggerRefresh(){
			// REFRESH
			this.$emit('refresh')
			if (typeof this.refresh == "function") this.refresh();
		},
		setProcessing(mode) {
			this.isProcessing = mode;
		},
		extractForm,
		isFormValid() {
			let validity = true;
			_.forEach(this.form, (value, keyname) => {
				let source = _.find(this.source, { name: keyname });
				if (
					!!this.$refs[keyname] &&
					this.$refs[keyname].length &&
					!this.$refs[keyname][0].isReadOnly() &&
					(value === null || value === "") &&
					typeof source == "object" &&
					source.required
				) {
					// alert(keyname)
					validity = false;
				}
			});
			return validity;
		},
		cleanModal() {
			this.isLoaded = false;
			this.$emit('closed')
		},
		async openModal() {
			this.form = _.clone(extractForm(this.formArchetype));
			this.$emit("input", this.form);
			this.source = [];
			this.isLoading = true;
			await this.define();
			this.isLoading = false;
		},
		async delItem($event) {
			let swalResponse = await swal({
				icon: 'info',
				title: "Are you sure you want to continue?",
				text: this.deleteDisclaimer,
				buttons: {
					cancel: "Cancel",
					danger: "Proceed",
				},
			});
			if (swalResponse == "danger") {
				this.isProcessing = true;
				$event.preventDefault();

				try {
					let response = await axios.delete(this.handler);
					// ALL OKAY
					if (response.status) {
						this.$bvModal.hide(this.currentId);
						this.triggerRefresh()
					}
				} catch (message) {
					console.error(message);
					mobiscroll.snackbar({
						display: "top",
						theme: "ios",
						message,
						color: "warning",
						duration: 7000,
					});
				}
				this.isProcessing = false;
			}
		},
		async define() {
			try {
				let response = await axios.get(this.handler);

				if (response.status && !!response.originalData) {
					this.form = _.merge(this.form, response.originalData);
				}

				if (this.formOverride != {}) {
					_.forEach(this.formOverride, (item, keyname) => {
						console.log(`item, keyname`, item, keyname);
						this.form[keyname] = item;
					});
				}

				if (response.status && !!response.source) {
					this.source = response.source;
				}

				this.asides = (response.status && !!response.asides) ? response.asides : null;

				this.$emit('defined');
				
				this.isLoaded = true;
			} catch (message) {
				console.error(message);
				mobiscroll.snackbar({
					display: "top",
					theme: "ios",
					message,
					color: "warning",
					duration: 7000,
				});
			}
		},
		async onSubmit($event) {
			if (!this.isFormValid()) {
				Toast.fire({
					type: "info",
					title: "Para continuar, llena los datos requeridos",
				});
			} else {
				this.isProcessing = true;
				$event.preventDefault();
				await this._performSubmit();
				this.isProcessing = false;
			}
		},
		async _performSubmit() {
			try {
				let data2send = _.clone(this.form);
				data2send._route = _.cloneDeep(this.$route.params);
				const response = await axios.post(this.handler, data2send);

				// ALL OKAY
				if (response.status) {
					this.$bvModal.hide(this.currentId);
					this.triggerRefresh()
				}
			} catch (message) {
				console.error(message);
				mobiscroll.snackbar({
					display: "top",
					theme: "ios",
					message,
					color: "warning",
					duration: 7000,
				});
			}
		},
		updateFromModel(){
			if(JSON.stringify(this.form) != JSON.stringify(this.value)){
				this.form = _.clone(this.value)
			}
		}
	},
	watch: {
		// FIX QUE PERMITE LA ACTUALIZACIÓN DEL VALOR LOCAL DESDE AFUERA
		value(){
			if(this.isModalOn){
				console.log('cloning from outside')
				this.form = _.cloneDeep(this.value)
			}
		},
		isLoaded() {
			if (this.isLoaded) {
				setTimeout(() => {
					this.$bvModal.show(this.currentId);
				}, 150);
			}
		},
		isModalOn() {
			if (this.isModalOn) {
				this.$emit("input", this.form);
			} else {
				this.$emit("input", {});
			}
		}
	},
	data: () => {
		return {
			currentId: false,
			isModalOn: false,
			inputEls: {},
			lodash: _,
			isLoading: false,
			isLoaded: false,
			isProcessing: false,
			form: null,
			source: [],
			asides: null
		};
	},
	async beforeMount() {
		this.currentId = _.uniqueId("modal-form-");
	},
	updated(){
		if(JSON.stringify(this.form) !== JSON.stringify(this.value) && this.isModalOn){
			this.$emit('input', this.form)
		}
	},
	mounted() {
		this.$root.$on('bv::modal::shown', (bvEvent, modalId) => {
			if(modalId == this.currentId){
				console.log('Fully shown!')
				this.$emit('display')
			}
		})
		this.$root.$on('bv::modal::hidden', (bvEvent, modalId) => {
			if(modalId == this.currentId){
				this.$emit('hidden', bvEvent, modalId)
			}
		})
  }
};
</script>